/**
 * SmartSearch.tsx
 * ───────────────
 * La SmartSearchBar est maintenant directement le chat guidé par IA.
 * La barre de recherche classique est supprimée.
 *
 * Court-circuit automatique si l'utilisateur saisit :
 *   - un numéro de pourvoi  (ex: 23-15.234 / 23-15-234)
 *   - un numéro RG de CA    (ex: 20/00915)
 *   - un ECLI               (ex: ECLI:FR:CCASS:2023:SO00123)
 *
 * Usage :
 *   <SmartSearchBar onSmartSearch={handleSmartSearch} />
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/Logo';
import type { AdvancedFilters } from './AdvancedSearch';

// ── Patterns identifiants directs ─────────────────────────────────────────────

/** Numéro de pourvoi CC : 23-15.234 / 23-15-234 / 17.83-620 */
const POURVOI_RE = /^\d{2}[.-]\d{2,3}[.-]\d{3,4}$/;

/** Numéro RG Cour d'appel (saisi seul) : 20/00915 */
const RG_RE      = /^\d{2}\/\d{4,6}$/;

/** ECLI : ECLI:FR:CCASS:2023:SO00123 */
const ECLI_RE    = /^ECLI:/i;

function isDirectIdentifier(text: string): boolean {
  const t = text.trim();
  return POURVOI_RE.test(t) || RG_RE.test(t) || ECLI_RE.test(t);
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface Message {
  role:     'assistant' | 'user';
  content:  string;
  pending?: boolean;
}

export type SmartSearchContext = 'jurisprudence' | 'rgpd' | 'cjue' | 'reglements';

interface SearchProposal {
  query:   string;
  filters: Partial<AdvancedFilters> & Record<string, string>;
  summary: string;
}

// ── API proxy ─────────────────────────────────────────────────────────────────

async function callClaude(
  messages: { role: string; content: string }[],
  context?: SmartSearchContext,
): Promise<string> {
  const response = await fetch('/api/fiches/smart-search/chat', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ messages, context }),
  });
  if (!response.ok) throw new Error(`API error ${response.status}`);
  const data = await response.json();
  return data.content ?? '';
}

async function fixJsonWithLLM(raw: string): Promise<string | null> {
  try {
    const response = await fetch('/api/fiches/smart-search/chat', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        messages: [{
          role:    'user',
          content: `Extrait uniquement le JSON valide de ce texte et retourne-le brut sans markdown ni backticks :\n\n${raw}`,
        }],
        model: 'mistralai/mistral-small-3.1-24b-instruct',
      }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.content ?? null;
  } catch { return null; }
}

// ── Parse JSON robuste ────────────────────────────────────────────────────────

function tryParseSearchProposal(raw: string): SearchProposal | null {
  const codeBlockMatch = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);

  const attempts = [
    codeBlockMatch ? codeBlockMatch[1].trim() : '',
    raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '').trim(),
    raw.includes('{') && raw.includes('}')
      ? raw.slice(raw.indexOf('{'), raw.lastIndexOf('}') + 1)
      : '',
    raw.match(/\{[^{}]*"ready"\s*:\s*true[\s\S]*?\}/)?.[0] ?? '',
    raw.match(/\{[^{}]*"direct_lookup"\s*:\s*true[\s\S]*?\}/)?.[0] ?? '',
    raw.trim(),
  ];

  for (const attempt of attempts) {
    if (!attempt) continue;
    try {
      const json = JSON.parse(attempt);
      // Cas direct_lookup retourné par le LLM
      if (json.direct_lookup && json.identifier) {
        return {
          query:   json.identifier,
          filters: { direct_lookup: true } as any,
          summary: json.identifier,
        };
      }
      // Cas recherche guidée classique
      if (json.ready && json.query) {
        return { query: json.query, filters: json.filters ?? {}, summary: json.summary ?? json.query };
      }
    } catch { /* continuer */ }
  }
  return null;
}

// ── Composant principal ───────────────────────────────────────────────────────

export interface SmartSearchBarProps {
  onSmartSearch:  (query: string, filters: Record<string, string>) => void;
  searching?:     boolean;
  launchedQuery?: string | null;
  context?:       SmartSearchContext;
}

export const SmartSearchBar = ({ onSmartSearch, searching = false, launchedQuery = null, context = 'jurisprudence' }: SmartSearchBarProps) => (
  <SmartSearchChat onSearch={onSmartSearch} searching={searching} launchedQuery={launchedQuery} context={context} />
);

// ── MdText — rendu minimal markdown (bold + sauts de ligne) ──────────────────

const MdText = ({ text }: { text: string }) => {
  const parts: React.ReactNode[] = [];
  const segments = text.split(/(\*\*[^*]+\*\*)/g);
  segments.forEach((seg, i) => {
    if (seg.startsWith('**') && seg.endsWith('**')) {
      parts.push(<strong key={i}>{seg.slice(2, -2)}</strong>);
    } else {
      seg.split('\n').forEach((line, j, arr) => {
        parts.push(<span key={`${i}-${j}`}>{line}</span>);
        if (j < arr.length - 1) parts.push(<br key={`${i}-${j}-br`} />);
      });
    }
  });
  return <>{parts}</>;
};

// ── Label identifiant ─────────────────────────────────────────────────────────

/** Retourne la clé i18n du libellé d'identifiant détecté */
function identifierLabelKey(text: string): string {
  const t = text.trim();
  if (ECLI_RE.test(t))   return 'search.smart.id_ecli';
  if (RG_RE.test(t))     return 'search.smart.id_rg';
  return 'search.smart.id_pourvoi';
}

// ── Chat interne ──────────────────────────────────────────────────────────────

// Clés i18n des messages d'accueil (résolues au rendu)
const WELCOME_MESSAGES: Record<SmartSearchContext, string> = {
  jurisprudence: 'search.smart.welcome_jurisprudence',
  rgpd:          'search.smart.welcome_rgpd',
  cjue:          'search.smart.welcome_cjue',
  reglements:    'search.smart.welcome_reglements',
};

interface SmartSearchChatProps {
  onSearch:       (query: string, filters: Record<string, string>) => void;
  searching?:     boolean;
  launchedQuery?: string | null;
  context?:       SmartSearchContext;
}

const SmartSearchChat = ({ onSearch, searching = false, launchedQuery = null, context = 'jurisprudence' }: SmartSearchChatProps) => {
  const { t } = useTranslation();
  const [messages,  setMessages]  = useState<Message[]>([]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [proposal,  setProposal]  = useState<SearchProposal | null>(null);
  const [launched,  setLaunched]  = useState(false);
  const messagesRef  = useRef<HTMLDivElement>(null);
  const inputRef     = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Gestion badge externe ──────────────────────────────────────────────────
  useEffect(() => {
    if (!launchedQuery?.trim()) return;

    const q = launchedQuery.trim();
    setLaunched(true);
    setProposal({ query: q, filters: {}, summary: q });
  }, [launchedQuery]);

  // Scroll le container dans le viewport quand l'input prend le focus
  const scrollContainerIntoView = () => {
    setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Message d'amorce (adapté au contexte)
  useEffect(() => {
    setMessages([{ role: 'assistant', content: t(WELCOME_MESSAGES[context] ?? WELCOME_MESSAGES.jurisprudence) }]);
    // Pas de focus automatique — évite le scroll au chargement de la page
  }, [context, t]);

  // Scroll interne uniquement
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const history = useCallback(() =>
    messages.filter(m => !m.pending).map(m => ({ role: m.role, content: m.content })),
  [messages]);

  // ── Court-circuit identifiant direct ──────────────────────────────────────
  const launchDirect = useCallback((text: string) => {
    const q = text.trim();
    setInput('');
    setLaunched(true);
    setProposal({ query: q, filters: {}, summary: q });
    onSearch(q, {});
  }, [onSearch]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    // ── Identifiant direct → pas de LLM ───────────────────────────────────
    if (isDirectIdentifier(text)) {
      launchDirect(text);
      return;
    }

    setInput('');
    setMessages(prev => [
      ...prev,
      { role: 'user',      content: text },
      { role: 'assistant', content: '...', pending: true },
    ]);
    setLoading(true);

    try {
      const reply = await callClaude([...history(), { role: 'user', content: text }], context);

      let parsed: SearchProposal | null = tryParseSearchProposal(reply);

      if (!parsed && (reply.includes('"ready"') || reply.includes('"query"')) && reply.includes('{')) {
        try {
          const fixed = await fixJsonWithLLM(reply);
          if (fixed) parsed = tryParseSearchProposal(fixed);
        } catch { /* silencieux */ }
      }

      const displayContent = parsed
        ? parsed.summary
        : (reply.includes('"ready"') && reply.includes('{'))
          ? t('search.smart.preparing')
          : reply;

      setMessages(prev => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: displayContent },
      ]);
      if (parsed) {
        setProposal(parsed);
        setTimeout(() => {
          containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 150);
      }

    } catch {
      setMessages(prev => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: t('search.smart.error') },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [input, loading, history, launchDirect, t]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); send(); }
  };

  // ── Détection live sur l'input (feedback visuel) ───────────────────────────
  const isDirect = isDirectIdentifier(input);

  const handleValidate = () => {
    if (!proposal) return;
    setLaunched(true);
    // Passe tous les filtres extraits par le LLM tels quels — chaque page gère son propre mapping
    const filters: Record<string, string> = {};
    for (const [k, v] of Object.entries(proposal.filters)) {
      if (v !== null && v !== undefined) filters[k] = String(v);
    }
    // ── Log usage agent ────────────────────────────────────────────────────
    const t0agent = performance.now();
    fetch('/api/fiches/log-agent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query:            proposal.query,
        fiche_context:    context,
        response_time_ms: Math.round(performance.now() - t0agent),
      }),
    }).catch(() => {});
    onSearch(proposal.query, filters);
  };

  const handleModify = () => {
    setProposal(null);
    setMessages(prev => [...prev, { role: 'assistant', content: t('search.smart.modify_prompt') }]);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleReset = () => {
    setProposal(null);
    setLaunched(false);
    setMessages([{ role: 'assistant', content: t('search.smart.welcome_jurisprudence') }]);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ── Mode compact ──────────────────────────────────────────────────────────
  if (launched && proposal) {
    return (
      <div ref={containerRef} className="rounded-2xl border-2 border-primary/20 bg-primary/5 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {searching ? (
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
          ) : (
            <Logo className="h-4 w-auto shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold text-primary truncate">
              {searching ? t('search.smart.in_progress') : t('search.smart.launched')}
            </p>
            <p className="text-xs text-muted-foreground truncate">"{proposal.query}"</p>
          </div>
          {/* Badges filtres inline */}
          <div className="hidden sm:flex items-center gap-1 flex-wrap">
            {proposal.filters.domaine && (
              <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                {proposal.filters.domaine}
              </span>
            )}
            {proposal.filters.jurisdiction && (
              <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full whitespace-nowrap">
                {proposal.filters.jurisdiction}
              </span>
            )}
            {proposal.filters.date_from && (
              <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full whitespace-nowrap">
                {t('search.smart.since', { date: proposal.filters.date_from })}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={handleReset}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0 flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {t('search.new_search')}
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="rounded-xl border border-border/80 bg-card shadow-sm overflow-hidden">

      {/* Messages */}
      <div
        ref={messagesRef}
        className="flex flex-col gap-2.5 px-4 pt-3 pb-2 overflow-y-auto"
        style={{ maxHeight: '220px' }}
      >
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`
              max-w-[80%] text-sm px-3.5 py-2 rounded-2xl leading-relaxed
              ${m.role === 'user'
                ? 'bg-primary text-primary-foreground rounded-br-sm'
                : 'bg-muted text-foreground rounded-bl-sm'
              }
            `}>
              {m.pending ? (
                <span className="flex gap-1 items-center h-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              ) : (
                <MdText text={m.content} />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Proposition de recherche */}
      {proposal && (
        <div className="mx-4 mb-3 p-3 rounded-xl border border-primary/30 bg-primary/5">
          <p className="text-xs font-semibold text-primary mb-1.5">{t('search.smart.proposed')}</p>
          <p className="text-sm text-foreground font-medium mb-2">"{proposal.query}"</p>
          <div className="flex flex-wrap gap-1 mb-3">
            {proposal.filters.domaine && (
              <span className="text-[11px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded-full">
                {proposal.filters.domaine}
              </span>
            )}
            {proposal.filters.solution && (
              <span className="text-[11px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full">
                {proposal.filters.solution}
              </span>
            )}
            {proposal.filters.jurisdiction && (
              <span className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                {proposal.filters.jurisdiction}
              </span>
            )}
            {proposal.filters.date_from && (
              <span className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                {t('search.smart.since', { date: proposal.filters.date_from })}
              </span>
            )}
            {proposal.filters.date_to && (
              <span className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                {t('search.smart.until', { date: proposal.filters.date_to })}
              </span>
            )}
            {proposal.filters.chamber && (
              <span className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                {proposal.filters.chamber}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={handleValidate}
              className="flex-1 text-sm py-1.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">
              {t('search.smart.launch')}
            </button>
            <button onClick={handleModify}
              className="text-sm px-4 py-1.5 rounded-lg border hover:bg-muted transition-colors">
              {t('search.smart.modify')}
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      {!proposal && (
        <div className={`flex items-center gap-2 px-3 py-2.5 border-t transition-colors ${isDirect ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/40' : 'bg-background border-border/60'}`}>
          <div className="flex-1 flex items-center gap-2 min-w-0">
            {isDirect && (
              <span className="shrink-0 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 rounded-full whitespace-nowrap">
                {t(identifierLabelKey(input))}
              </span>
            )}
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('search.smart.placeholder')}
              disabled={loading}
              onFocus={scrollContainerIntoView}
              className="flex-1 h-8 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:opacity-50"
            />
          </div>
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            title={isDirect ? t('search.smart.search_direct') : t('common.send')}
            className={`w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-40 transition-all
              ${isDirect
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
              }`}
          >
            {isDirect ? (
              /* icône loupe */
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
            ) : (
              /* icône flèche */
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};